<script setup lang="ts">
import { computed } from "vue";
import {
  AlertTriangle,
  CheckCircle2,
  TimerReset,
  WalletCards,
  XCircle,
} from "lucide-vue-next";
import type { RenewalKpiCounts } from "@/utils/renewalMetrics";

const props = defineProps<{
  counts: RenewalKpiCounts;
  interactive?: boolean;
  activeFilter?: keyof RenewalKpiCounts | "";
}>();

const emit = defineEmits<{
  (event: "select-filter", filter: keyof RenewalKpiCounts): void;
}>();

const cards = computed(() => [
  {
    filter: "dueThisMonth" as const,
    label: "Clients Due This Month",
    value: props.counts.dueThisMonth,
    icon: WalletCards,
    iconClass: "bg-[#eef4ff] text-[#2855a6]",
  },
  {
    label: "Renewals Next Month",
    filter: "renewalsNextMonth" as const,
    value: props.counts.renewalsNextMonth,
    icon: TimerReset,
    iconClass: "bg-[#f6f0ff] text-[#6d4ad3]",
  },
  {
    label: "Overdue Renewals",
    filter: "overdueRenewals" as const,
    value: props.counts.overdueRenewals,
    icon: AlertTriangle,
    iconClass: "bg-[#fff4e8] text-[#b45309]",
  },
  {
    label: "Recently Renewed",
    filter: "recentlyRenewed" as const,
    value: props.counts.recentlyRenewed,
    icon: CheckCircle2,
    iconClass: "bg-[#ecfdf3] text-[#047857]",
  },
  {
    label: "Expired Accounts",
    filter: "expiredAccounts" as const,
    value: props.counts.expiredAccounts,
    icon: XCircle,
    iconClass: "bg-[#fff1f1] text-[#b42318]",
  },
]);
</script>

<template>
  <section
    class="grid gap-4 md:grid-cols-2 xl:grid-cols-5"
    aria-label="Renewal tracking KPIs"
  >
    <component
      :is="interactive ? 'button' : 'article'"
      v-for="card in cards"
      :key="card.label"
      :type="interactive ? 'button' : undefined"
      :aria-pressed="interactive ? activeFilter === card.filter : undefined"
      :data-filter="card.filter"
      :class="interactive ? 'text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2' : ''"
      class="rounded-[1.25rem] border border-[rgba(218,226,238,0.95)] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-5 shadow-[0_18px_36px_rgba(15,23,42,0.06)]"
      @click="interactive && emit('select-filter', card.filter)"
    >
      <div class="flex items-start justify-between gap-4">
        <div>
          <p
            class="text-[0.76rem] font-extrabold uppercase tracking-[0.08em] text-[#6f7b91]"
          >
            {{ card.label }}
          </p>
          <p class="mt-3 text-[1.9rem] font-extrabold leading-none text-[#182033]">
            {{ card.value }}
          </p>
        </div>

        <div
          class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
          :class="card.iconClass"
        >
          <component
            :is="card.icon"
            class="size-5"
            aria-hidden="true"
          />
        </div>
      </div>
    </component>
  </section>
</template>
